import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { unwrapJoin } from "@/utils/unwrapJoin";
import { logger } from "@/lib/logger";

interface OrganizationContextType {
  orgId: string | null;
  orgName: string | null;
  loading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType>({
  orgId: null,
  orgName: null,
  loading: true,
});

export function useCurrentOrganization() {
  return useContext(OrganizationContext);
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchOrg() {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        let employeeQuery = supabase
          .from("employees")
          .select("organization_id, organizations(name)")
          .limit(1);

        // En preview/dev il n'y a pas toujours de session auth active
        if (user) {
          employeeQuery = employeeQuery.eq("user_id", user.id);
        }

        const { data: employee } = await employeeQuery.maybeSingle();

        if (!cancelled && employee) {
          setOrgId(employee.organization_id);
          const org = unwrapJoin<{ name: string }>(employee.organizations);
          setOrgName(org?.name ?? null);
          setLoading(false);
          return;
        }

        // Fallback: récupérer la première organisation disponible
        if (!user) {
          const { data: organization } = await supabase
            .from("organizations")
            .select("id, name")
            .limit(1)
            .maybeSingle();

          if (!cancelled && organization) {
            setOrgId(organization.id);
            setOrgName(organization.name);
          }
        }

        if (!cancelled) setLoading(false);
      } catch (err: unknown) {
        logger.error("OrganizationProvider fetch error", { stack: err instanceof Error ? err.stack : undefined });
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchOrg();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchOrg();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <OrganizationContext.Provider value={{ orgId, orgName, loading }}>
      {children}
    </OrganizationContext.Provider>
  );
}
